import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search, UserPlus, Loader2, Mail, AtSign, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/supabase';

/**
 * FriendInviteDialog v2 — primary path is **live profile search**.
 *
 * Behaviour:
 *   1. As the user types (debounced 300ms, min 2 chars) we call
 *      `api.searchProfiles(query)` which returns at most 10 profiles
 *      that match the name OR handle, EXCLUDING the caller and anyone
 *      already in a friendship row.
 *   2. Tapping a row sends a friend request via `api.sendFriendInvite` →
 *      that profile immediately shows up in the caller's friends list with
 *      status='pending'.
 *   3. If the search returns nothing, we surface a fallback "Invite by
 *      email/username" link to the legacy path.
 */
export function FriendInviteDialog({ open, onOpenChange, onSent }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pendingId, setPendingId] = useState(null); // id of profile being invited
  const [sentIds, setSentIds] = useState(new Set()); // profiles we've just invited
  const [mode, setMode] = useState('search'); // 'search' | 'email' | 'handle'
  const [email, setEmail] = useState('');
  const [handle, setHandle] = useState('');
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setMode('search');
      setEmail('');
      setHandle('');
      setSentIds(new Set());
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (mode !== 'search') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const rows = await api.searchProfiles(query.trim(), 10);
        setResults(rows);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, mode]);

  async function inviteProfile(profile) {
    setPendingId(profile.id);
    try {
      // Send via handle (always present) — server resolves to profile row.
      const cleanHandle = (profile.handle || '').replace(/^@/, '');
      await api.sendFriendInvite({ handle: cleanHandle });
      setSentIds((prev) => new Set(prev).add(profile.id));
      toast.success(`Friend request sent to ${profile.name}`);
      onSent?.();
    } catch (err) {
      toast.error(err.message || 'Failed to send');
    } finally {
      setPendingId(null);
    }
  }

  async function submitFallback() {
    setBusy(true);
    try {
      const res = await api.sendFriendInvite({
        email: mode === 'email' ? email.trim() : null,
        handle: mode === 'handle' ? handle.trim() : null,
      });
      if (res.mode === 'request') toast.success('Friend request sent');
      else toast.success("Invite sent — they'll see it when they sign up");
      setEmail('');
      setHandle('');
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="friend-invite-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">Add a friend</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {mode === 'search' ? 'Search for someone by name or @handle.' : `Send an invite by ${mode}.`}
          </DialogDescription>
        </DialogHeader>

        {mode === 'search' && (
          <>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a name or @handle…"
                className="pl-9 h-11 rounded-md"
                data-testid="friend-search-input"
                autoFocus
              />
            </div>

            <div className="min-h-[140px] max-h-[300px] overflow-y-auto" data-testid="friend-search-results">
              {searching && (
                <div className="py-8 flex justify-center" data-testid="friend-search-loading">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!searching && query.trim().length < 2 && (
                <p className="text-xs text-muted-foreground py-8 text-center">
                  Type at least 2 characters to start searching.
                </p>
              )}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <div className="py-6 text-center" data-testid="friend-search-empty">
                  <p className="text-xs text-muted-foreground">No one found for "{query}".</p>
                  <button
                    type="button"
                    onClick={() => setMode('email')}
                    className="mt-2 text-xs text-primary underline underline-offset-2"
                    data-testid="friend-search-fallback-email"
                  >
                    Invite by email instead →
                  </button>
                </div>
              )}
              {!searching && results.length > 0 && (
                <ul className="divide-y divide-border rivl-card">
                  {results.map((r) => {
                    const sent = sentIds.has(r.id);
                    const busyRow = pendingId === r.id;
                    return (
                      <li
                        key={r.id}
                        className="flex items-center gap-3 px-3 py-2.5"
                        data-testid={`friend-search-result-${r.id}`}
                      >
                        <Avatar name={r.name} src={r.avatarUrl} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{r.name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{r.handle || '—'}</div>
                        </div>
                        {sent ? (
                          <span
                            data-testid={`friend-search-pending-${r.id}`}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-dashed border-muted-foreground/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground"
                          >
                            <Clock className="h-3 w-3" /> Pending
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            disabled={busyRow}
                            onClick={() => inviteProfile(r)}
                            data-testid={`friend-search-invite-${r.id}`}
                            className="h-8 rounded-md"
                          >
                            {busyRow ? <Loader2 className="h-3 w-3 animate-spin" /> : <><UserPlus className="h-3 w-3 mr-1" /> Add</>}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="pt-2 border-t border-border flex items-center justify-center gap-3 text-[11px] text-muted-foreground">
              <button
                type="button"
                onClick={() => setMode('email')}
                className="hover:text-foreground underline-offset-2 inline-flex items-center gap-1"
                data-testid="friend-mode-email"
              >
                <Mail className="h-3 w-3" /> Invite by email
              </button>
              <span>·</span>
              <button
                type="button"
                onClick={() => setMode('handle')}
                className="hover:text-foreground underline-offset-2 inline-flex items-center gap-1"
                data-testid="friend-mode-handle"
              >
                <AtSign className="h-3 w-3" /> Invite by handle
              </button>
            </div>
          </>
        )}

        {mode === 'email' && (
          <FallbackForm
            label="Email address"
            placeholder="friend@example.com"
            value={email}
            onChange={setEmail}
            onSubmit={submitFallback}
            onBack={() => setMode('search')}
            busy={busy}
            testidInput="invite-email-input"
            testidSubmit="invite-send-btn"
          />
        )}

        {mode === 'handle' && (
          <FallbackForm
            label="Username"
            placeholder="amara_okafor"
            value={handle}
            onChange={(v) => setHandle(v.replace(/^@/, ''))}
            onSubmit={submitFallback}
            onBack={() => setMode('search')}
            busy={busy}
            mono
            testidInput="invite-handle-input"
            testidSubmit="invite-send-btn"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FallbackForm({ label, placeholder, value, onChange, onSubmit, onBack, busy, mono, testidInput, testidSubmit }) {
  const canSubmit = value && value.trim().length > 0;
  return (
    <>
      <div className="space-y-2">
        <label className="rivl-label text-[10px]">{label}</label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`h-11 ${mono ? 'font-mono' : ''}`}
          data-testid={testidInput}
          autoFocus
        />
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Button variant="outline" onClick={onBack} disabled={busy} className="flex-1">
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit || busy}
          data-testid={testidSubmit}
          className="flex-1"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-2" /> Send</>}
        </Button>
      </div>
    </>
  );
}

function Avatar({ name, src }) {
  const initials = (name || 'U').split(' ').map((n) => n[0]).slice(0, 2).join('');
  if (src) {
    return (
      <div
        className="h-9 w-9 rounded-md border border-border bg-cover bg-center"
        style={{ backgroundImage: `url(${src})` }}
      />
    );
  }
  return (
    <div className="h-9 w-9 rounded-md bg-secondary flex items-center justify-center text-xs font-bold">
      {initials}
    </div>
  );
}
